import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, SignIn } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Erro ao autenticar";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email.trim(), password);
      toast.success("Bem-vindo(a)");
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--emerald)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-10" />
        <div className="relative">
          <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_locacao-shield/artifacts/5k0zetdw_logo%20safimob%2003.JPG" alt="Safimob" className="h-14 rounded-md" />
        </div>
        <div className="relative">
          <div className="eyebrow text-white/60 mb-3">Plataforma de garantia locatícia</div>
          <h1 className="font-display text-5xl tracking-tighter leading-[0.95]">
            Gestão completa<br />
            de garantia<br />
            locatícia.
          </h1>
          <p className="text-white/70 mt-6 max-w-md leading-relaxed">
            Análise de crédito, contratos, assinatura eletrônica e cobrança automática — tudo em um só painel.
          </p>
        </div>
        <div className="relative text-xs text-white/40">Safimob © 2026 — Garantidora de Aluguel</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_locacao-shield/artifacts/5k0zetdw_logo%20safimob%2003.JPG" alt="Safimob" className="h-12" />
          </div>
          <div className="eyebrow mb-2">Acesso restrito</div>
          <h2 className="font-display text-4xl tracking-tight">Entrar</h2>
          <p className="text-slate-600 mt-2 text-sm">Acesso exclusivo para a equipe Safimob.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Email</Label>
              <Input data-testid="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@imobiliaria.com" className="h-11" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Senha</Label>
              <div className="relative">
                <Input data-testid="login-password" type={show ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">{error}</div>}

            <Button type="submit" data-testid="login-submit" disabled={loading} className="btn-primary w-full h-11">
              <SignIn size={16} className="mr-2" /> {loading ? "Entrando…" : "Entrar"}
            </Button>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">← Voltar ao site</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
